import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Chip,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

/** q
 * Form for editing schema item properties
 */
export function StructuredOutputForm({ selectedItem, onUpdateItem }) {
  const { formatMessage: msg } = useIntl();
  const [newEnumValue, setNewEnumValue] = useState('');
  const propertyTypes = ['boolean', 'string', 'enum', 'integer', 'number', 'object', 'array'];

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      name: selectedItem?.name || '',
      type: selectedItem?.type || 'string',
      description: selectedItem?.description || '',
      required: selectedItem?.required || false,
      enum: selectedItem?.enum || []
    }
  });

  const currentType = watch('type');
  const currentEnum = watch('enum') || [];

  useEffect(() => {
    if (selectedItem) {
      reset({
        name: selectedItem.name || '',
        type: selectedItem.type || 'string',
        description: selectedItem.description || '',
        required: selectedItem.required || false,
        enum: selectedItem.enum || []
      });
    }
  }, [selectedItem, reset]);

  const onSubmit = (data) => {
    if (!selectedItem) return;

    onUpdateItem({
      ...selectedItem,
      name: data.name,
      type: data.type,
      description: data.description,
      required: data.required,
      enum: data.type === 'enum' ? data.enum : undefined
    });
  };

  const addEnumValue = () => {
    if (newEnumValue.trim() !== '' && !currentEnum.includes(newEnumValue)) {
      const updatedEnum = [...currentEnum, newEnumValue];
      reset({ ...watch(), enum: updatedEnum });
      setNewEnumValue('');
    }
  };

  const removeEnumValue = (valueToRemove) => {
    const updatedEnum = currentEnum.filter((value) => value !== valueToRemove);
    reset({ ...watch(), enum: updatedEnum });
  };

  if (!selectedItem) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text">
          {msg({ id: 'field.output.empty.description' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="name"
        control={control}
        rules={{ required: 'Name is required' }}
        render={({ field, fieldState: { error } }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.output.name.label' })}
            variant="filled"
            size="small"
            fullWidth
            margin="normal"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        name="type"
        control={control}
        render={({ field }) => (
          <FormControl variant="filled" fullWidth sx={{ flexDirection: 'row' }}>
            <InputLabel id="type-label">{msg({ id: 'field.output.type.label' })}</InputLabel>
            <Select
              {...field}
              label={msg({ id: 'field.output.type.label' })}
              size="small"
              required
              sx={{ flex: 1 }}
            >
              {propertyTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {msg({ id: `field.output.type.${type}.label` })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {currentType === 'enum' && (
        <Box sx={{ mt: 1 }}>
          <MDTypography variant="button">
            {msg({ id: 'field.output.enum.values.title' })}
          </MDTypography>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={newEnumValue}
              onChange={(e) => setNewEnumValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEnumValue();
                }
              }}
              label={msg({ id: 'field.output.enum.new.value' })}
              variant="filled"
              size="small"
              fullWidth
            />
            <Tooltip title={msg({ id: 'tooltip.add.value' })}>
              <Fab size="small" color="primary" aria-label="add" onClick={addEnumValue}>
                <AddIcon fontSize="small" />
              </Fab>
            </Tooltip>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {currentEnum.map((value) => (
              <Chip
                key={value}
                label={value}
                onDelete={() => removeEnumValue(value)}
                deleteIcon={<DeleteIcon color="error" />}
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.output.description.label' })}
            variant="filled"
            size="small"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            sx={{ marginTop: 1 }}
          />
        )}
      />

      <Box sx={{ mt: 1.5, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <MDButton type="submit" variant="contained" color="primary">
          {msg({ id: 'action.save' })}
        </MDButton>
      </Box>
    </Box>
  );
}

StructuredOutputForm.propTypes = {
  selectedItem: PropTypes.object,
  onUpdateItem: PropTypes.func.isRequired
};
